import React, { useState, useRef, useCallback } from 'react';
import { 
  Play, Pause, SkipBack, SkipForward, Scissors, Layers, 
  Wand2, Sparkles, Settings, Download, MonitorPlay,
  Volume2, Video, Type, Music, Mic, Captions, Eraser, Zap, Sliders, 
  Trash2, Copy, Undo, Redo, SplitSquareHorizontal, Maximize, 
  CheckCircle, Loader2, Plus, Diamond, FileArchive, Minimize, Eye, EyeOff,
  Lock, Unlock, GripVertical, Film, Image, Volume1, VolumeX, RotateCcw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

let clipCounter = 6;

const CLIPS_INIT = [
  { id: 'clip-1', name: 'Main_Sequence_01.mp4', type: 'video', track: 'V1', start: 0, duration: 25, color: '#00F0FF', source: 'https://storage.googleapis.com/gtv-videos-bucket/sample/TearsOfSteel.mp4', visible: true, locked: false, properties: { scale: 100, opacity: 100, brightness: 100, contrast: 100, saturation: 100, blur: 0, hue: 0 } },
  { id: 'clip-2', name: 'B-Roll_Cityscape.mp4', type: 'video', track: 'V1', start: 25, duration: 15, color: '#003B6F', visible: true, locked: false, properties: { scale: 100, opacity: 100, brightness: 100, contrast: 100, saturation: 100, blur: 0, hue: 0 } },
  { id: 'clip-3', name: 'Voiceover_Take1.wav', type: 'audio', track: 'A1', start: 2, duration: 35, color: '#22C55E', visible: true, locked: false, properties: { scale: 100, opacity: 100, brightness: 100, contrast: 100, saturation: 100, blur: 0, hue: 0, volume: 100 } },
  { id: 'clip-4', name: 'Ambient_Pad.mp3', type: 'audio', track: 'A2', start: 0, duration: 60, color: '#22C55E', visible: true, locked: false, properties: { scale: 100, opacity: 50, brightness: 100, contrast: 100, saturation: 100, blur: 0, hue: 0, volume: 60 } },
  { id: 'clip-5', name: 'Title: "The Awakening"', type: 'text', track: 'V2', start: 5, duration: 10, color: '#F59E0B', visible: true, locked: false, properties: { scale: 100, opacity: 100, brightness: 100, contrast: 100, saturation: 100, blur: 0, hue: 0 } },
];

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const f = Math.floor((seconds % 1) * 24);
  return `00:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}:${String(f).padStart(2,'0')}`;
};

const LUT_PRESETS = {
  'Cinematic': { brightness: 95, contrast: 120, saturation: 85, hue: -5 },
  'Cyberpunk': { brightness: 105, contrast: 130, saturation: 140, hue: 180 },
  'Vintage': { brightness: 110, contrast: 90, saturation: 60, hue: 25 },
  'B&W': { brightness: 105, contrast: 115, saturation: 0, hue: 0 },
  'Teal & Orange': { brightness: 100, contrast: 110, saturation: 130, hue: 160 },
  'Matrix': { brightness: 90, contrast: 140, saturation: 120, hue: 90 },
};

export default function AIEditorPage() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState('library');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration] = useState(60);
  const [selectedClipId, setSelectedClipId] = useState('clip-1');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [clips, setClips] = useState(CLIPS_INIT);
  const [toasts, setToasts] = useState([]);
  const [history, setHistory] = useState([CLIPS_INIT]);
  const [historyIdx, setHistoryIdx] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [appliedLUT, setAppliedLUT] = useState(null);
  const [activeModifiers, setActiveModifiers] = useState(new Set());
  const [dragClipId, setDragClipId] = useState(null);
  const videoRef = useRef(null);
  const previewRef = useRef(null);

  const selectedClip = clips.find(c => c.id === selectedClipId);

  const pushHistory = useCallback((newClips) => {
    setHistory(prev => [...prev.slice(0, historyIdx + 1), newClips]);
    setHistoryIdx(prev => prev + 1);
    setClips(newClips);
  }, [historyIdx]);

  const undo = () => {
    if (historyIdx > 0) {
      setHistoryIdx(historyIdx - 1);
      setClips(history[historyIdx - 1]);
      showToast('Undo', 'info', 1500);
    }
  };

  const redo = () => {
    if (historyIdx < history.length - 1) {
      setHistoryIdx(historyIdx + 1);
      setClips(history[historyIdx + 1]);
      showToast('Redo', 'info', 1500);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
  };

  const handleTimelineClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const t = Math.max(0, Math.min(pct * duration, duration));
    setCurrentTime(t);
    if (videoRef.current) videoRef.current.currentTime = t;
  };

  const showToast = (msg, type = 'info', ms = 3000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, msg, type }]);
    if (type !== 'loading') setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), ms);
    return id;
  };
  const dismissToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));

  const simulateAI = (task, success, onComplete) => {
    const id = showToast(`${task}...`, 'loading');
    setTimeout(() => {
      dismissToast(id);
      showToast(success, 'success');
      if (onComplete) onComplete();
    }, 2500);
  };

  const updateProp = (prop, val) => {
    if (!selectedClipId) return;
    const newClips = clips.map(c => c.id === selectedClipId ? { ...c, properties: { ...c.properties, [prop]: val } } : c);
    pushHistory(newClips);
  };

  // Clip operations
  const splitClip = () => {
    if (!selectedClip || selectedClip.locked) return;
    const splitPoint = currentTime - selectedClip.start;
    if (splitPoint <= 0.5 || splitPoint >= selectedClip.duration - 0.5) {
      showToast('Move playhead to split point within clip', 'info'); return;
    }
    const newId = `clip-${clipCounter++}`;
    const newClips = clips.flatMap(c => {
      if (c.id !== selectedClipId) return [c];
      return [
        { ...c, duration: splitPoint },
        { ...c, id: newId, name: `${c.name.replace(/\.\w+$/, '')}_B${c.name.match(/\.\w+$/)?.[0] || ''}`, start: c.start + splitPoint, duration: c.duration - splitPoint },
      ];
    });
    pushHistory(newClips);
    showToast('Clip split at playhead', 'success', 2000);
  };

  const duplicateClip = () => {
    if (!selectedClip || selectedClip.locked) return;
    const newId = `clip-${clipCounter++}`;
    const newClip = { ...selectedClip, id: newId, name: `${selectedClip.name} (copy)`, start: selectedClip.start + selectedClip.duration, properties: { ...selectedClip.properties } };
    pushHistory([...clips, newClip]);
    setSelectedClipId(newId);
    showToast('Clip duplicated', 'success', 2000);
  };

  const deleteClip = () => {
    if (!selectedClip || selectedClip.locked) return;
    const newClips = clips.filter(c => c.id !== selectedClipId);
    pushHistory(newClips);
    setSelectedClipId(newClips[0]?.id || null);
    showToast('Clip deleted', 'info', 2000);
  };

  const toggleVisibility = (clipId) => {
    const newClips = clips.map(c => c.id === clipId ? { ...c, visible: !c.visible } : c);
    pushHistory(newClips);
  };

  const toggleLock = (clipId) => {
    const newClips = clips.map(c => c.id === clipId ? { ...c, locked: !c.locked } : c);
    pushHistory(newClips);
  };

  const applyLUT = (name) => {
    if (!selectedClip || selectedClip.type !== 'video') {
      showToast('Select a video clip first', 'info'); return;
    }
    const preset = LUT_PRESETS[name];
    simulateAI(`Applying ${name} LUT`, `${name} LUT applied`, () => {
      const newClips = clips.map(c => c.id === selectedClipId ? { ...c, properties: { ...c.properties, ...preset } } : c);
      pushHistory(newClips);
      setAppliedLUT(name);
    });
  };

  const resetEffects = () => {
    if (!selectedClip) return;
    const newClips = clips.map(c => c.id === selectedClipId ? { ...c, properties: { ...c.properties, brightness: 100, contrast: 100, saturation: 100, blur: 0, hue: 0 } } : c);
    pushHistory(newClips);
    setAppliedLUT(null);
    showToast('Effects reset', 'info', 2000);
  };

  const addTextClip = (label) => {
    const newId = `clip-${clipCounter++}`;
    const newClip = { id: newId, name: `Title: "${label}"`, type: 'text', track: 'V2', start: currentTime, duration: 8, color: '#F59E0B', visible: true, locked: false, properties: { scale: 100, opacity: 100, brightness: 100, contrast: 100, saturation: 100, blur: 0, hue: 0 } };
    pushHistory([...clips, newClip]);
    setSelectedClipId(newId);
    showToast(`${label} title added at ${formatTime(currentTime).substring(3,8)}`, 'success', 2000);
  };

  const toggleModifier = (name) => {
    setActiveModifiers(prev => {
      const s = new Set(prev);
      if (s.has(name)) s.delete(name); else s.add(name);
      return s;
    });
  };

  const handleExport = (fmt) => {
    setShowExportMenu(false);
    simulateAI(`Encoding ${fmt}`, `Export complete — ${fmt} ready for download.`);
  };

  const handleClipDrag = (clipId, e) => {
    const clip = clips.find(c => c.id === clipId);
    if (!clip || clip.locked) return;
    setDragClipId(clipId);
    const startX = e.clientX;
    const origStart = clip.start;
    const timelineEl = e.currentTarget.closest('[data-track-area]');
    if (!timelineEl) return;
    const trackWidth = timelineEl.getBoundingClientRect().width;

    const onMove = (ev) => {
      const delta = ev.clientX - startX;
      const timeDelta = (delta / trackWidth) * duration;
      const newStart = Math.max(0, Math.min(origStart + timeDelta, duration - clip.duration));
      setClips(prev => prev.map(c => c.id === clipId ? { ...c, start: newStart } : c));
    };
    const onUp = () => {
      setDragClipId(null);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Compute preview filter from selected clip
  const getPreviewFilter = () => {
    if (!selectedClip || selectedClip.type !== 'video') return {};
    const p = selectedClip.properties;
    return {
      filter: `brightness(${p.brightness}%) contrast(${p.contrast}%) saturate(${p.saturation}%) blur(${p.blur}px) hue-rotate(${p.hue}deg)`,
      opacity: p.opacity / 100,
      transform: `scale(${p.scale / 100})`,
    };
  };

  // Active text overlays at current time
  const activeTextClips = clips.filter(c => c.type === 'text' && c.visible && currentTime >= c.start && currentTime <= c.start + c.duration);

  const TABS = [
    { id: 'library', icon: Layers, label: 'Media' },
    { id: 'ai', icon: Wand2, label: 'AI Tools' },
    { id: 'generate', icon: Sparkles, label: 'Generate' },
    { id: 'text', icon: Type, label: 'Text' },
    { id: 'effects', icon: Zap, label: 'Effects' },
  ];

  const TRACKS = [
    { key: 'V2', label: 'V2', icon: Type, type: 'text' },
    { key: 'V1', label: 'V1', icon: Video, type: 'video' },
    { key: 'A1', label: 'A1', icon: Mic, type: 'audio' },
    { key: 'A2', label: 'A2', icon: Music, type: 'audio' },
  ];

  return (
    <div className={`flex flex-col overflow-hidden ${isFullscreen ? 'fixed inset-0 z-[9999]' : 'h-[calc(100vh-64px)]'}`} style={{ background: '#0A0A0C', color: '#D1D5DB' }} data-testid="ai-editor-page">
      {/* Toast Container */}
      <div className="fixed top-16 right-4 z-[10000] flex flex-col gap-2 pointer-events-none">
        <AnimatePresence>
          {toasts.map(toast => (
            <motion.div key={toast.id} initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 50 }}
              className="px-4 py-3 rounded-xl shadow-lg border flex items-center gap-3 pointer-events-auto"
              style={{
                background: toast.type === 'success' ? 'rgba(34,197,94,0.15)' : toast.type === 'loading' ? 'rgba(0,240,255,0.1)' : 'rgba(30,30,30,0.9)',
                borderColor: toast.type === 'success' ? 'rgba(34,197,94,0.3)' : toast.type === 'loading' ? 'rgba(0,240,255,0.2)' : '#2A2A2A',
                color: toast.type === 'success' ? '#22C55E' : toast.type === 'loading' ? '#00F0FF' : '#E5E7EB',
              }}>
              {toast.type === 'loading' && <Loader2 className="w-4 h-4 animate-spin" />}
              {toast.type === 'success' && <CheckCircle className="w-4 h-4" />}
              {toast.type === 'info' && <Sparkles className="w-4 h-4" />}
              <span className="text-sm font-medium">{toast.msg}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Top Nav */}
      <header className="h-12 border-b flex items-center justify-between px-4 shrink-0" style={{ borderColor: '#1A1A1A', background: '#111114' }}>
        <div className="flex items-center gap-4">
          <Link to="/" className="text-sm font-medium hover:text-white transition-colors" style={{ color: '#6B7280' }}>&larr; Dashboard</Link>
          <div className="h-4 w-px" style={{ background: '#2A2A2A' }} />
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color: '#00F0FF' }} />
            <span className="text-sm font-bold" style={{ fontFamily: "'Orbitron', sans-serif", color: '#00F0FF', letterSpacing: '0.08em' }}>SMITHERS STUDIO</span>
            <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ml-1" style={{ background: 'rgba(0,240,255,0.1)', color: '#00F0FF', border: '1px solid rgba(0,240,255,0.2)' }}>v3.1</span>
          </div>
          {appliedLUT && (
            <span className="px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1.5" style={{ background: 'rgba(139,92,246,0.1)', color: '#8B5CF6', border: '1px solid rgba(139,92,246,0.3)' }}>
              <Film className="w-3 h-3" /> LUT: {appliedLUT}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 relative">
          <span className="text-xs font-mono hidden sm:block" style={{ color: '#4B5563' }}>
            {clips.length} clips · {formatTime(duration).substring(3,8)}
          </span>
          <button onClick={() => setIsFullscreen(p => !p)} className="p-1.5 rounded transition-colors hover:text-white hover:bg-[rgba(255,255,255,0.05)]" style={{ color: '#6B7280' }} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
            {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </button>
          <div className="relative">
            <button onClick={() => setShowExportMenu(!showExportMenu)}
              className="px-4 py-1.5 text-sm font-bold rounded-lg transition-colors flex items-center gap-2"
              style={{ background: 'linear-gradient(135deg, #00F0FF, #003B6F)', color: '#000', boxShadow: '0 0 12px rgba(0,240,255,0.2)' }}
              data-testid="export-project-btn">
              <Download className="w-4 h-4" /> Export
            </button>
            {showExportMenu && (
              <div className="absolute top-full right-0 mt-2 w-48 rounded-xl shadow-xl overflow-hidden z-50" style={{ background: '#1A1A1A', border: '1px solid #2A2A2A' }}>
                <div className="p-2 space-y-1">
                  {['MP4 (H.264)', 'WEBM (VP9)', 'ProRes 4444', 'Animated GIF'].map(f => (
                    <button key={f} onClick={() => handleExport(f)} className="w-full text-left px-3 py-2 text-sm rounded-lg transition-colors hover:bg-[rgba(0,240,255,0.05)]" style={{ color: '#D1D5DB' }}>{f}</button>
                  ))}
                  <div className="h-px my-1" style={{ background: '#2A2A2A' }} />
                  <button onClick={() => handleExport('ZIP Archive')} className="w-full text-left px-3 py-2 text-sm rounded-lg transition-colors hover:bg-[rgba(0,240,255,0.05)] flex items-center gap-2" style={{ color: '#D1D5DB' }}>
                    <FileArchive className="w-4 h-4" /> ZIP Archive
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Workspace */}
      <div className="flex flex-1 overflow-hidden">

        {/* Left Sidebar */}
        <div className="w-72 border-r flex flex-col shrink-0 hidden lg:flex" style={{ borderColor: '#1A1A1A', background: '#111114' }} data-testid="editor-sidebar">
          <div className="flex border-b overflow-x-auto shrink-0" style={{ borderColor: '#1A1A1A' }}>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="flex-1 py-3 px-2 flex flex-col items-center gap-1 min-w-[56px] transition-colors"
                style={{
                  color: activeTab === tab.id ? '#00F0FF' : '#6B7280',
                  borderBottom: activeTab === tab.id ? '2px solid #00F0FF' : '2px solid transparent',
                  background: activeTab === tab.id ? 'rgba(0,240,255,0.03)' : 'transparent',
                }}
                data-testid={`tab-${tab.id}`}>
                <tab.icon className="w-4 h-4" />
                <span className="text-[10px] font-bold uppercase tracking-wider">{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {activeTab === 'library' && (
              <div className="space-y-4">
                <button className="w-full py-2.5 border border-dashed rounded-xl text-sm transition-all flex items-center justify-center gap-2 hover:border-[#00F0FF] hover:text-[#00F0FF]" style={{ borderColor: '#2A2A2A', color: '#6B7280' }} data-testid="import-media-btn">
                  <Plus className="w-4 h-4" /> Import Media
                </button>
                <div>
                  <h3 className="text-[10px] font-bold uppercase tracking-wider mb-3 flex justify-between" style={{ color: '#6B7280' }}>
                    Project Files <span>{clips.length} items</span>
                  </h3>
                  <div className="space-y-1.5">
                    {clips.map(clip => (
                      <div key={clip.id}
                        onClick={() => setSelectedClipId(clip.id)}
                        className="flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-all"
                        style={{
                          background: selectedClipId === clip.id ? `${clip.color}12` : 'transparent',
                          border: `1px solid ${selectedClipId === clip.id ? clip.color + '30' : 'transparent'}`,
                        }}>
                        <div className="w-6 h-6 rounded flex items-center justify-center shrink-0" style={{ background: `${clip.color}15` }}>
                          {clip.type === 'video' && <Video className="w-3 h-3" style={{ color: clip.color }} />}
                          {clip.type === 'audio' && <Music className="w-3 h-3" style={{ color: clip.color }} />}
                          {clip.type === 'text' && <Type className="w-3 h-3" style={{ color: clip.color }} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs truncate" style={{ color: selectedClipId === clip.id ? '#fff' : '#D1D5DB' }}>{clip.name}</p>
                          <p className="text-[9px] font-mono" style={{ color: '#4B5563' }}>
                            {formatTime(clip.start).substring(3,8)} — {formatTime(clip.start + clip.duration).substring(3,8)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={(e) => { e.stopPropagation(); toggleVisibility(clip.id); }} className="p-0.5 rounded hover:bg-[rgba(255,255,255,0.05)]" style={{ color: clip.visible ? '#6B7280' : '#EF4444' }}>
                            {clip.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); toggleLock(clip.id); }} className="p-0.5 rounded hover:bg-[rgba(255,255,255,0.05)]" style={{ color: clip.locked ? '#F59E0B' : '#6B7280' }}>
                            {clip.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'ai' && (
              <div className="space-y-3">
                <div className="p-4 rounded-xl" style={{ background: 'rgba(0,240,255,0.05)', border: '1px solid rgba(0,240,255,0.1)' }}>
                  <Sparkles className="w-5 h-5 mb-2" style={{ color: '#00F0FF' }} />
                  <h4 className="text-sm font-bold text-white mb-1">Creative Director</h4>
                  <p className="text-xs mb-3" style={{ color: '#6B7280' }}>Analyze timeline pacing, color grading, and audio mixing.</p>
                  <button onClick={() => simulateAI('Analyzing timeline structure', '3 suggestions: trim clip-2 by 3s, boost A1 gain +6dB, add color transition at 25s.')}
                    className="w-full py-2 text-xs font-bold rounded-lg transition-all hover:scale-[1.01]"
                    style={{ background: 'linear-gradient(135deg, #00F0FF, #003B6F)', color: '#000' }} data-testid="ai-analyze-btn">
                    Analyze Timeline
                  </button>
                </div>
                {[
                  { icon: Scissors, label: 'Auto-Cut Silences', desc: 'Remove dead air automatically.', task: 'Detecting silences in A1 track', done: 'Removed 4.2s of silence from 3 segments.', color: '#8B5CF6' },
                  { icon: Captions, label: 'Auto-Captions', desc: 'Generate timed subtitles.', task: 'Transcribing speech from Voiceover_Take1', done: '47 captions generated and placed on V2 track.', color: '#00F0FF' },
                  { icon: Eraser, label: 'Remove Background', desc: 'AI rotoscoping on selected clip.', task: 'Segmenting subject in Main_Sequence_01', done: 'Background removed. Alpha channel generated.', color: '#22C55E' },
                  { icon: Mic, label: 'Studio Voice', desc: 'Enhance audio clarity and remove noise.', task: 'Processing noise reduction on A1', done: 'Noise floor reduced by 18dB. Clarity enhanced.', color: '#F59E0B' },
                ].map((tool, i) => (
                  <button key={i} onClick={() => simulateAI(tool.task, tool.done)}
                    className="w-full p-3 rounded-xl flex items-start gap-3 text-left transition-all hover:bg-[rgba(255,255,255,0.02)] hover:scale-[1.01] active:scale-[0.99]"
                    style={{ background: 'rgba(20,20,20,0.5)', border: '1px solid #1A1A1A' }} data-testid={`ai-tool-${i}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${tool.color}15` }}>
                      <tool.icon className="w-4 h-4" style={{ color: tool.color }} />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-white">{tool.label}</div>
                      <div className="text-xs mt-0.5" style={{ color: '#6B7280' }}>{tool.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {activeTab === 'generate' && (
              <div className="space-y-4">
                <div className="p-4 rounded-xl" style={{ background: '#141414', border: '1px solid #1A1A1A' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Video className="w-4 h-4" style={{ color: '#00F0FF' }} />
                    <h4 className="text-sm font-bold text-white">Generate B-Roll</h4>
                    <span className="text-[9px] px-1.5 py-0.5 rounded ml-auto" style={{ background: 'rgba(0,240,255,0.1)', color: '#00F0FF' }}>Veo 3.1</span>
                  </div>
                  <textarea placeholder="Describe the scene you need..." className="w-full h-20 rounded-lg p-2 text-sm text-white outline-none resize-none mb-3" style={{ background: '#0A0A0C', border: '1px solid #2A2A2A' }} data-testid="generate-broll-input" />
                  <div className="flex gap-2 mb-3">
                    {['4s', '8s', '16s'].map(d => (
                      <button key={d} className="flex-1 py-1.5 rounded-lg text-[10px] font-bold transition-colors hover:border-[#00F0FF]" style={{ background: '#0A0A0C', border: '1px solid #2A2A2A', color: '#6B7280' }}>{d}</button>
                    ))}
                  </div>
                  <button onClick={() => simulateAI('Generating video with Veo 3.1', 'B-Roll generated — added to V1 track.', () => {
                    const newId = `clip-${clipCounter++}`;
                    pushHistory([...clips, { id: newId, name: 'AI_Generated_BRoll.mp4', type: 'video', track: 'V1', start: 40, duration: 8, color: '#8B5CF6', visible: true, locked: false, properties: { scale: 100, opacity: 100, brightness: 100, contrast: 100, saturation: 100, blur: 0, hue: 0 } }]);
                  })} className="w-full py-2 text-xs font-bold rounded-lg flex items-center justify-center gap-2 transition-all hover:scale-[1.01]" style={{ background: 'linear-gradient(135deg, #00F0FF, #003B6F)', color: '#000' }} data-testid="generate-broll-btn">
                    <Sparkles className="w-4 h-4" /> Generate Video
                  </button>
                </div>
                <div className="p-4 rounded-xl" style={{ background: '#141414', border: '1px solid #1A1A1A' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Volume2 className="w-4 h-4" style={{ color: '#22C55E' }} />
                    <h4 className="text-sm font-bold text-white">AI Voiceover</h4>
                  </div>
                  <textarea placeholder="Enter text for AI voiceover..." className="w-full h-16 rounded-lg p-2 text-sm text-white outline-none resize-none mb-3" style={{ background: '#0A0A0C', border: '1px solid #2A2A2A' }} data-testid="voiceover-input" />
                  <select className="w-full rounded-lg px-2 py-1.5 text-xs outline-none mb-3" style={{ background: '#0A0A0C', border: '1px solid #2A2A2A', color: '#D1D5DB' }}>
                    <option>Voice: Zephyr (Male, Confident)</option>
                    <option>Voice: Puck (Female, Warm)</option>
                    <option>Voice: Charon (Deep, Cinematic)</option>
                    <option>Voice: Kore (Female, Energetic)</option>
                  </select>
                  <button onClick={() => simulateAI('Synthesizing speech', 'Voiceover generated — added to A1 track.', () => {
                    const newId = `clip-${clipCounter++}`;
                    pushHistory([...clips, { id: newId, name: 'AI_Voiceover.wav', type: 'audio', track: 'A1', start: currentTime, duration: 12, color: '#22C55E', visible: true, locked: false, properties: { scale: 100, opacity: 100, brightness: 100, contrast: 100, saturation: 100, blur: 0, hue: 0, volume: 100 } }]);
                  })} className="w-full py-2 text-xs font-bold rounded-lg flex items-center justify-center gap-2 transition-all hover:scale-[1.01]" style={{ background: '#22C55E', color: '#000' }} data-testid="generate-voiceover-btn">
                    <Mic className="w-4 h-4" /> Generate Audio
                  </button>
                </div>
                <div className="p-4 rounded-xl" style={{ background: '#141414', border: '1px solid #1A1A1A' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Image className="w-4 h-4" style={{ color: '#FF00E5' }} />
                    <h4 className="text-sm font-bold text-white">AI Thumbnail</h4>
                  </div>
                  <button onClick={() => simulateAI('Generating thumbnail from keyframe', 'Thumbnail exported as PNG.')}
                    className="w-full py-2 text-xs font-bold rounded-lg flex items-center justify-center gap-2 transition-all hover:scale-[1.01]" style={{ background: 'rgba(255,0,229,0.15)', color: '#FF00E5', border: '1px solid rgba(255,0,229,0.3)' }}>
                    <Sparkles className="w-4 h-4" /> Generate from Current Frame
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'effects' && (
              <div className="space-y-4">
                <h3 className="text-[10px] font-bold uppercase tracking-wider flex items-center justify-between" style={{ color: '#6B7280' }}>
                  Color Grading LUTs
                  {appliedLUT && <button onClick={resetEffects} className="text-[9px] px-2 py-0.5 rounded flex items-center gap-1 hover:bg-[rgba(239,68,68,0.1)]" style={{ color: '#EF4444' }}><RotateCcw className="w-3 h-3" /> Reset</button>}
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {Object.keys(LUT_PRESETS).map(effect => (
                    <div key={effect} onClick={() => applyLUT(effect)}
                      className="aspect-video rounded-lg flex items-center justify-center cursor-pointer transition-all relative overflow-hidden group hover:scale-105 active:scale-95"
                      style={{
                        background: '#141414', border: appliedLUT === effect ? '2px solid #8B5CF6' : '1px solid #1A1A1A',
                        boxShadow: appliedLUT === effect ? '0 0 12px rgba(139,92,246,0.3)' : 'none',
                      }} data-testid={`lut-${effect.toLowerCase().replace(/[&\s]/g, '-')}`}>
                      <img src={`https://picsum.photos/seed/${effect}/150/100`} className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-opacity mix-blend-overlay" alt={effect} />
                      <span className="text-xs font-medium z-10 drop-shadow-md text-white">{effect}</span>
                      {appliedLUT === effect && <CheckCircle className="absolute top-1 right-1 w-4 h-4 z-10" style={{ color: '#8B5CF6' }} />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'text' && (
              <div className="space-y-3">
                <h3 className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Text & Titles</h3>
                {[
                  { label: 'Basic Title', desc: 'Centered title overlay', icon: Type },
                  { label: 'Lower Third', desc: 'Name/title bar at bottom', icon: Captions },
                  { label: 'End Card', desc: 'Outro card with CTA', icon: Film },
                  { label: 'Subtitle', desc: 'Manual subtitle at playhead', icon: Captions },
                ].map(({ label, desc, icon: Icon }) => (
                  <button key={label} onClick={() => addTextClip(label)}
                    className="w-full py-3 px-3 text-sm rounded-lg transition-all hover:bg-[rgba(0,240,255,0.03)] hover:scale-[1.01] active:scale-[0.99] flex items-center gap-3 text-left"
                    style={{ background: '#141414', border: '1px solid #1A1A1A', color: '#D1D5DB' }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(245,158,11,0.1)' }}>
                      <Icon className="w-4 h-4" style={{ color: '#F59E0B' }} />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-[10px]" style={{ color: '#6B7280' }}>{desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Center - Preview & Timeline */}
        <div className="flex-1 flex flex-col min-w-0" style={{ background: '#0A0A0C' }}>
          {/* Toolbar */}
          <div className="h-9 border-b flex items-center px-4 justify-between shrink-0" style={{ borderColor: '#1A1A1A', background: '#0E0E10' }}>
            <div className="flex items-center gap-1">
              <button onClick={undo} disabled={historyIdx <= 0} className="p-1.5 rounded transition-colors hover:text-white hover:bg-[rgba(255,255,255,0.05)] disabled:opacity-20" style={{ color: '#6B7280' }} title="Undo (Ctrl+Z)" data-testid="undo-btn"><Undo className="w-4 h-4" /></button>
              <button onClick={redo} disabled={historyIdx >= history.length - 1} className="p-1.5 rounded transition-colors hover:text-white hover:bg-[rgba(255,255,255,0.05)] disabled:opacity-20" style={{ color: '#6B7280' }} title="Redo (Ctrl+Y)" data-testid="redo-btn"><Redo className="w-4 h-4" /></button>
              <div className="w-px h-4 mx-2" style={{ background: '#2A2A2A' }} />
              <button onClick={splitClip} className="p-1.5 rounded transition-colors hover:text-white hover:bg-[rgba(255,255,255,0.05)]" style={{ color: '#6B7280' }} title="Split at Playhead" data-testid="split-btn"><SplitSquareHorizontal className="w-4 h-4" /></button>
              <button onClick={duplicateClip} className="p-1.5 rounded transition-colors hover:text-white hover:bg-[rgba(255,255,255,0.05)]" style={{ color: '#6B7280' }} title="Duplicate" data-testid="copy-btn"><Copy className="w-4 h-4" /></button>
              <button onClick={deleteClip} className="p-1.5 rounded transition-colors hover:text-red-400 hover:bg-[rgba(239,68,68,0.05)]" style={{ color: '#6B7280' }} title="Delete" data-testid="delete-btn"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-mono" style={{ color: '#4B5563' }}>1920x1080 · 24fps</span>
              <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ background: '#141414', color: '#00F0FF' }}>{formatTime(currentTime)}</span>
            </div>
          </div>

          {/* Video Preview */}
          <div ref={previewRef} className="flex-1 p-3 flex flex-col items-center justify-center relative overflow-hidden">
            <div className="aspect-video w-full max-w-4xl rounded-lg shadow-2xl relative overflow-hidden" style={{ background: '#000', border: '1px solid #1A1A1A' }} data-testid="video-preview">
              {selectedClip?.source ? (
                <video ref={videoRef} src={selectedClip.source} className="w-full h-full object-contain" style={getPreviewFilter()} onTimeUpdate={handleTimeUpdate} />
              ) : (
                <div className="w-full h-full flex items-center justify-center" style={{ background: '#0A0A0C' }}>
                  <MonitorPlay className="w-16 h-16" style={{ color: '#1A1A1A' }} />
                </div>
              )}
              {/* Text overlays */}
              {activeTextClips.map(tc => (
                <div key={tc.id} className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <h1 className="text-4xl sm:text-6xl font-bold text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.9)] tracking-widest uppercase" style={{ fontFamily: "'Orbitron', sans-serif", textShadow: '0 0 30px rgba(0,240,255,0.3)' }}>
                    {tc.name.replace('Title: "', '').replace('"', '')}
                  </h1>
                </div>
              ))}
              {/* Timecode overlay */}
              <div className="absolute bottom-3 left-3 px-2 py-1 rounded text-xs font-mono flex items-center gap-2" style={{ background: 'rgba(0,0,0,0.8)', color: '#D1D5DB' }}>
                {isPlaying && <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#EF4444' }} />}
                {formatTime(currentTime)}
              </div>
              {/* Applied effects indicator */}
              {selectedClip && selectedClip.type === 'video' && (selectedClip.properties.brightness !== 100 || selectedClip.properties.contrast !== 100 || selectedClip.properties.saturation !== 100 || selectedClip.properties.hue !== 0 || selectedClip.properties.blur > 0) && (
                <div className="absolute top-3 right-3 px-2 py-1 rounded text-[10px] font-bold flex items-center gap-1.5" style={{ background: 'rgba(139,92,246,0.2)', color: '#8B5CF6', border: '1px solid rgba(139,92,246,0.3)' }}>
                  <Zap className="w-3 h-3" /> FX Active
                </div>
              )}
            </div>

            {/* Playback Controls */}
            <div className="mt-4 flex items-center gap-5 px-5 py-2 rounded-full" style={{ background: '#111114', border: '1px solid #1A1A1A' }}>
              <button onClick={() => { setCurrentTime(0); if (videoRef.current) videoRef.current.currentTime = 0; }} className="p-2 transition-colors hover:text-white" style={{ color: '#6B7280' }}>
                <SkipBack className="w-4 h-4" />
              </button>
              <button onClick={togglePlay} className="p-3 rounded-full transition-all hover:scale-105 active:scale-95" style={{ background: 'linear-gradient(135deg, #00F0FF, #FF00E5)', color: '#000', boxShadow: '0 0 16px rgba(0,240,255,0.3)' }} data-testid="play-btn">
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </button>
              <button onClick={() => { setCurrentTime(duration); if (videoRef.current) videoRef.current.currentTime = duration; }} className="p-2 transition-colors hover:text-white" style={{ color: '#6B7280' }}>
                <SkipForward className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Timeline */}
          <div className="h-56 border-t flex flex-col shrink-0" style={{ borderColor: '#1A1A1A', background: '#111114' }} data-testid="timeline">
            {/* Ruler */}
            <div className="h-7 border-b flex items-end px-4 relative cursor-crosshair" style={{ borderColor: '#1A1A1A', background: '#0E0E10' }} onClick={handleTimelineClick}>
              <div className="w-20 shrink-0" />
              <div className="flex-1 relative h-full">
                {[...Array(13)].map((_, i) => (
                  <div key={i} className="absolute flex flex-col items-center bottom-0.5" style={{ left: `${(i / 12) * 100}%` }}>
                    <span className="text-[8px] font-mono mb-0.5" style={{ color: '#4B5563' }}>{formatTime((i / 12) * duration).substring(3, 8)}</span>
                    <div className="w-px h-2" style={{ background: '#2A2A2A' }} />
                  </div>
                ))}
                <div className="absolute top-0 bottom-0 w-px z-50 pointer-events-none" style={{ left: `${(currentTime / duration) * 100}%`, background: '#FF00E5', boxShadow: '0 0 6px rgba(255,0,229,0.5)' }}>
                  <div className="w-2.5 h-2.5 rounded-sm rotate-45 -ml-[4px] -mt-0.5" style={{ background: '#FF00E5' }} />
                </div>
              </div>
            </div>

            {/* Tracks */}
            <div className="flex-1 overflow-y-auto p-1.5 space-y-1">
              {TRACKS.map(track => {
                const trackClips = clips.filter(c => c.track === track.key);
                return (
                  <div key={track.key} className="flex h-11 gap-1.5 group" data-testid={`track-${track.label}`}>
                    <div className="w-20 shrink-0 rounded flex items-center justify-between px-2 text-xs font-bold transition-colors" style={{ background: '#141414', border: '1px solid #1A1A1A', color: '#6B7280' }}>
                      <span className="flex items-center gap-1"><track.icon className="w-3 h-3" /> {track.label}</span>
                    </div>
                    <div className="flex-1 rounded relative overflow-hidden" style={{ background: '#0C0C0C', border: '1px solid rgba(255,255,255,0.03)' }} data-track-area="true">
                      {trackClips.map(clip => (
                        <div
                          key={clip.id}
                          onClick={() => setSelectedClipId(clip.id)}
                          onMouseDown={(e) => { e.preventDefault(); handleClipDrag(clip.id, e); }}
                          className="absolute top-1 bottom-1 rounded flex items-center px-2 transition-all select-none"
                          style={{
                            left: `${(clip.start / duration) * 100}%`,
                            width: `${(clip.duration / duration) * 100}%`,
                            background: `${clip.color}18`,
                            borderLeft: `2px solid ${clip.color}`,
                            boxShadow: selectedClipId === clip.id ? `0 0 0 1px rgba(255,255,255,0.5), inset 0 0 8px ${clip.color}15` : `inset 0 0 8px ${clip.color}08`,
                            cursor: clip.locked ? 'not-allowed' : (dragClipId === clip.id ? 'grabbing' : 'grab'),
                            opacity: clip.visible ? 1 : 0.3,
                          }}
                          data-testid={`clip-${clip.id}`}>
                          {clip.type === 'audio' && (
                            <div className="absolute inset-0 flex items-center justify-around opacity-25 px-1">
                              {[...Array(20)].map((_, i) => <div key={i} className="w-0.5 rounded-full" style={{ height: `${Math.random() * 70 + 20}%`, background: clip.color }} />)}
                            </div>
                          )}
                          <span className="text-[9px] font-medium truncate relative z-10 flex items-center gap-1" style={{ color: selectedClipId === clip.id ? '#fff' : `${clip.color}CC` }}>
                            {clip.locked && <Lock className="w-2.5 h-2.5 shrink-0" />}
                            {clip.name}
                          </span>
                        </div>
                      ))}
                      <div className="absolute top-0 bottom-0 w-px opacity-30 pointer-events-none" style={{ left: `${(currentTime / duration) * 100}%`, background: '#FF00E5' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Sidebar - Inspector */}
        <div className="w-64 border-l flex flex-col shrink-0 hidden xl:flex" style={{ borderColor: '#1A1A1A', background: '#111114' }} data-testid="inspector-panel">
          <div className="h-10 border-b flex items-center px-4 shrink-0" style={{ borderColor: '#1A1A1A', background: '#0E0E10' }}>
            <h3 className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: '#6B7280' }}>
              <Sliders className="w-4 h-4" /> Inspector
            </h3>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {selectedClip ? (
              <div className="space-y-5">
                <div className="p-3 rounded-lg" style={{ background: '#141414', border: `1px solid ${selectedClip.color}30` }}>
                  <div className="text-[9px] uppercase tracking-wider font-bold mb-1 flex items-center justify-between" style={{ color: '#6B7280' }}>
                    <span>Selected Clip</span>
                    <span className="px-1.5 py-0.5 rounded" style={{ background: `${selectedClip.color}15`, color: selectedClip.color }}>{selectedClip.type.toUpperCase()}</span>
                  </div>
                  <div className="text-sm font-medium text-white truncate">{selectedClip.name}</div>
                  <div className="text-xs font-mono mt-1 flex items-center justify-between" style={{ color: '#6B7280' }}>
                    <span>In: {formatTime(selectedClip.start).substring(3, 8)}</span>
                    <span>Out: {formatTime(selectedClip.start + selectedClip.duration).substring(3, 8)}</span>
                  </div>
                  <div className="text-xs font-mono" style={{ color: '#4B5563' }}>
                    Duration: {formatTime(selectedClip.duration).substring(3, 8)}
                  </div>
                </div>

                {/* Transform */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-white border-b pb-2 flex items-center gap-2" style={{ borderColor: '#1A1A1A' }}>
                    <Settings className="w-3.5 h-3.5" style={{ color: '#00F0FF' }} /> Transform
                  </h4>
                  {[
                    { prop: 'scale', label: 'Scale', min: 10, max: 200, suffix: '%', color: '#00F0FF' },
                    { prop: 'opacity', label: 'Opacity', min: 0, max: 100, suffix: '%', color: '#00F0FF' },
                  ].map(({ prop, label, min, max, suffix, color }) => (
                    <div key={prop}>
                      <div className="flex justify-between text-xs mb-1" style={{ color: '#6B7280' }}>
                        <span className="flex items-center gap-1"><Diamond className="w-3 h-3" style={{ color }} /> {label}</span>
                        <span className="font-mono">{selectedClip.properties[prop]}{suffix}</span>
                      </div>
                      <input type="range" min={min} max={max} value={selectedClip.properties[prop]} onChange={e => updateProp(prop, parseInt(e.target.value))} className="w-full h-1.5 rounded-lg appearance-none cursor-pointer accent-cyan-500" style={{ background: '#1A1A1A' }} data-testid={`slider-${prop}`} />
                    </div>
                  ))}
                </div>

                {/* Audio volume for audio clips */}
                {selectedClip.type === 'audio' && (
                  <div className="space-y-3 pt-2">
                    <h4 className="text-sm font-medium text-white border-b pb-2 flex items-center gap-2" style={{ borderColor: '#1A1A1A' }}>
                      {selectedClip.properties.volume > 0 ? <Volume1 className="w-3.5 h-3.5" style={{ color: '#22C55E' }} /> : <VolumeX className="w-3.5 h-3.5" style={{ color: '#EF4444' }} />} Audio
                    </h4>
                    <div>
                      <div className="flex justify-between text-xs mb-1" style={{ color: '#6B7280' }}>
                        <span className="flex items-center gap-1"><Diamond className="w-3 h-3" style={{ color: '#22C55E' }} /> Volume</span>
                        <span className="font-mono">{selectedClip.properties.volume || 0}%</span>
                      </div>
                      <input type="range" min={0} max={200} value={selectedClip.properties.volume || 100} onChange={e => updateProp('volume', parseInt(e.target.value))} className="w-full h-1.5 rounded-lg appearance-none cursor-pointer accent-green-500" style={{ background: '#1A1A1A' }} data-testid="slider-volume" />
                    </div>
                  </div>
                )}

                {/* Color & Effects for video clips */}
                {selectedClip.type === 'video' && (
                  <div className="space-y-3 pt-2">
                    <h4 className="text-sm font-medium text-white border-b pb-2 flex items-center justify-between" style={{ borderColor: '#1A1A1A' }}>
                      <span className="flex items-center gap-2"><Zap className="w-3.5 h-3.5" style={{ color: '#FF00E5' }} /> Color & Effects</span>
                      <button onClick={resetEffects} className="text-[9px] px-1.5 py-0.5 rounded hover:bg-[rgba(239,68,68,0.1)]" style={{ color: '#6B7280' }}><RotateCcw className="w-3 h-3" /></button>
                    </h4>
                    {[
                      { prop: 'brightness', label: 'Brightness', min: 0, max: 200, suffix: '%' },
                      { prop: 'contrast', label: 'Contrast', min: 0, max: 200, suffix: '%' },
                      { prop: 'saturation', label: 'Saturation', min: 0, max: 200, suffix: '%' },
                      { prop: 'hue', label: 'Hue Rotate', min: -180, max: 180, suffix: '°' },
                      { prop: 'blur', label: 'Gaussian Blur', min: 0, max: 20, suffix: 'px' },
                    ].map(({ prop, label, min, max, suffix }) => (
                      <div key={prop}>
                        <div className="flex justify-between text-xs mb-1" style={{ color: '#6B7280' }}>
                          <span className="flex items-center gap-1"><Diamond className="w-3 h-3" style={{ color: '#FF00E5' }} /> {label}</span>
                          <span className="font-mono">{selectedClip.properties[prop]}{suffix}</span>
                        </div>
                        <input type="range" min={min} max={max} value={selectedClip.properties[prop]} onChange={e => updateProp(prop, parseInt(e.target.value))} className="w-full h-1.5 rounded-lg appearance-none cursor-pointer accent-fuchsia-500" style={{ background: '#1A1A1A' }} data-testid={`slider-${prop}`} />
                      </div>
                    ))}
                  </div>
                )}

                {/* AI Modifiers */}
                <div className="pt-3 border-t" style={{ borderColor: '#1A1A1A' }}>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: '#00F0FF' }}>
                    <Sparkles className="w-4 h-4" /> AI Modifiers
                  </h4>
                  <div className="space-y-2">
                    {[
                      { label: 'Upscale to 4K', task: 'Upscaling with ESRGAN', done: 'Resolution increased to 3840x2160.', icon: Maximize },
                      { label: 'Smooth Slow-Mo', task: 'Interpolating frames (RIFE)', done: '240fps slow-motion applied.', icon: Film },
                      { label: 'Object Tracking', task: 'Running SAM2 tracker', done: 'Subject locked. Auto-follow enabled.', icon: Eye },
                      { label: 'Chroma Key', task: 'Segmenting green screen', done: 'Background keyed. Alpha matte generated.', icon: Eraser },
                      { label: 'Stabilize', task: 'Analyzing motion vectors', done: 'Footage stabilized. Crop: 4%.', icon: GripVertical },
                    ].map(({ label, task, done, icon: Icon }) => {
                      const isActive = activeModifiers.has(label);
                      return (
                        <button key={label} onClick={() => {
                          if (isActive) { toggleModifier(label); showToast(`${label} disabled`, 'info', 1500); }
                          else simulateAI(task, done, () => toggleModifier(label));
                        }}
                          className="w-full py-2 px-3 text-xs font-medium rounded-lg transition-all hover:scale-[1.01] active:scale-[0.99] flex items-center gap-2.5"
                          style={{
                            background: isActive ? 'rgba(0,240,255,0.08)' : '#141414',
                            border: `1px solid ${isActive ? 'rgba(0,240,255,0.25)' : '#1A1A1A'}`,
                            color: isActive ? '#00F0FF' : '#D1D5DB',
                          }}
                          data-testid={`ai-mod-${label.toLowerCase().replace(/\s/g, '-')}`}>
                          <Icon className="w-4 h-4 shrink-0" style={{ color: isActive ? '#00F0FF' : '#6B7280' }} />
                          {label}
                          {isActive && <CheckCircle className="w-3 h-3 ml-auto shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-4" style={{ color: '#4B5563' }}>
                <MonitorPlay className="w-12 h-12 mb-3 opacity-20" />
                <p className="text-sm">Select a clip to inspect.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
